import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { examLessons } from '../config/ExamConfig';
import { collection, addDoc, getDocs, updateDoc, doc, query, where, deleteDoc } from "firebase/firestore";
import { db, auth } from "../firebase"; // auth'u import et
import { onAuthStateChanged } from "firebase/auth"; // onAuthStateChanged'ı import et
import { motion, AnimatePresence } from "framer-motion";

function LessonSelectPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const exam = searchParams.get('exam') || 'TYT–AYT';
  const [userId, setUserId] = useState(null); // userId state'e taşındı
  const [username, setUsername] = useState('');
  const [lessons, setLessons] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLessonName, setNewLessonName] = useState('');
  const [editLessonId, setEditLessonId] = useState(null);
  const [editLessonName, setEditLessonName] = useState('');
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true); // Yükleme durumu için state

  useEffect(() => {
    // Kimlik doğrulama durumunu kontrol et
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        const savedName = localStorage.getItem('username');
        if (savedName) {
           setUsername(savedName);
        }
      } else {
        setUserId(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchLessons = async () => {
      if (!userId) return;

      try {
        const q = query(collection(db, "lessons"), where("userId", "==", userId));
        const querySnapshot = await getDocs(q);
        const userLessons = [];
        querySnapshot.forEach((doc) => {
          userLessons.push({ id: doc.id, ...doc.data() });
        });

        if (userLessons.length === 0) {
          const defaultLessons = examLessons[exam] || [];
          for (const lesson of defaultLessons) {
            const lessonData = {
              userId,
              name: typeof lesson === 'object' ? lesson.name : lesson,
            };
            const docRef = await addDoc(collection(db, "lessons"), lessonData);
            userLessons.push({ id: docRef.id, ...lessonData });
          }
        }
        setLessons(userLessons);
      } catch (err) {
        setError(`Dersler çekilemedi: ${err.message}`);
      }
    };

    if (!loading) {
      fetchLessons();
    }
  }, [userId, exam, loading, navigate]);

  const handleAddLesson = async () => {
    if (!newLessonName) {
      setError("Ders adı zorunludur!");
      return;
    }

    try {
      const lessonData = {
        userId,
        name: newLessonName,
      };
      const docRef = await addDoc(collection(db, "lessons"), lessonData);
      setLessons([...lessons, { id: docRef.id, ...lessonData }]);
      setShowAddModal(false);
      setNewLessonName('');
    } catch (err) {
      setError(`Ders eklenemedi: ${err.message}`);
    }
  };

  const handleEditLesson = async (lessonId) => {
    if (!editLessonName) {
      setError("Ders adı zorunludur!");
      return;
    }

    try {
      const lessonRef = doc(db, "lessons", lessonId);
      await updateDoc(lessonRef, { name: editLessonName });
      setLessons(
        lessons.map((lesson) =>
          lesson.id === lessonId ? { ...lesson, name: editLessonName } : lesson
        )
      );
      setEditLessonId(null);
      setEditLessonName('');
    } catch (err) {
      setError(`Ders güncellenemedi: ${err.message}`);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    try {
      await deleteDoc(doc(db, "lessons", lessonId));
      setLessons(lessons.filter((lesson) => lesson.id !== lessonId));
    } catch (err) {
      setError(`Ders silinemedi: ${err.message}`);
    }
  };

  const handleClick = (lessonName) => {
    navigate(`/lesson/${lessonName.toLowerCase()}?exam=${exam}`);
  };

  const handleTrialClick = () => {
    navigate(`/trial?exam=${exam}`);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', color: '#f0f0f0' }}>Yükleniyor...</div>;
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <motion.div
        style={containerStyle}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Yıldız deseni için pseudo-eleman */}
        <div
          style={{
            content: "''",
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: "url('https://www.transparenttextures.com/patterns/stardust.png')",
            opacity: 0.1,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
        <motion.h1
          style={headerStyle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          Merhaba {username}, ne çalışmak istersin?
        </motion.h1>
        <motion.div
          style={addButtonContainerStyle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <motion.button
            onClick={() => setShowAddModal(true)}
            style={addButtonStyle}
            whileHover={{ scale: 1.05, backgroundColor: "#00c853" }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            + Ders Ekle
          </motion.button>
          <motion.button
            onClick={handleTrialClick}
            style={trialButtonStyle}
            whileHover={{ scale: 1.05, backgroundColor: "#0288d1" }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            📊 Deneme Takibi
          </motion.button>
        </motion.div>
        <motion.div
          style={cardsContainerStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <AnimatePresence>
            {lessons.map((lesson, index) => (
              <motion.div
                key={lesson.id}
                style={lessonCardStyle}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
              >
                {editLessonId === lesson.id ? (
                  <motion.div
                    style={{ display: "flex", gap: "8px", alignItems: "center" }}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.input
                      type="text"
                      value={editLessonName}
                      onChange={(e) => setEditLessonName(e.target.value)}
                      style={editInputStyle}
                      whileFocus={{ borderColor: "#00e676" }}
                      transition={{ duration: 0.2 }}
                    />
                    <motion.button
                      onClick={() => handleEditLesson(lesson.id)}
                      style={saveButtonStyle}
                      whileHover={{ scale: 1.05, backgroundColor: "#00c853" }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      Kaydet
                    </motion.button>
                    <motion.button
                      onClick={() => setEditLessonId(null)}
                      style={cancelButtonStyle}
                      whileHover={{ scale: 1.05, backgroundColor: "#e91e63" }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      İptal
                    </motion.button>
                  </motion.div>
                ) : (
                  <div style={lessonContentStyle}>
                    <span
                      onClick={() => handleClick(lesson.name)}
                      style={{ flex: 1, cursor: "pointer" }}
                    >
                      {lesson.name}
                    </span>
                    <motion.button
                      onClick={() => {
                        setEditLessonId(lesson.id);
                        setEditLessonName(lesson.name);
                      }}
                      style={editButtonStyle}
                      whileHover={{ scale: 1.05, backgroundColor: "#00c853" }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      Düzenle
                    </motion.button>
                    <motion.button
                      onClick={() => handleDeleteLesson(lesson.id)}
                      style={deleteButtonStyle}
                      whileHover={{ scale: 1.05, backgroundColor: "#e91e63" }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      Sil
                    </motion.button>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence>
          {showAddModal && (
            <motion.div
              style={modalOverlayStyle}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                style={modalStyle}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h3 style={modalTitleStyle}>Yeni Ders Ekle</h3>
                <motion.input
                  type="text"
                  placeholder="Ders Adı"
                  value={newLessonName}
                  onChange={(e) => setNewLessonName(e.target.value)}
                  style={modalInputStyle}
                  whileFocus={{ borderColor: "#00e676" }}
                  transition={{ duration: 0.2 }}
                />
                <div style={modalButtonContainerStyle}>
                  <motion.button
                    onClick={handleAddLesson}
                    style={modalSaveButtonStyle}
                    whileHover={{ scale: 1.05, backgroundColor: "#00c853" }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    Ekle
                  </motion.button>
                  <motion.button
                    onClick={() => setShowAddModal(false)}
                    style={modalCancelButtonStyle}
                    whileHover={{ scale: 1.05, backgroundColor: "#e91e63" }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    İptal
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {error && (
            <motion.p
              style={errorTextStyle}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

// Stil Tanımları
const containerStyle = {
  background: 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)',
  minHeight: '100vh',
  padding: '40px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: '#f0f0f0',
  fontFamily: "'Inter', sans-serif",
  position: 'relative',
  overflow: 'hidden',
};

const headerStyle = {
  textAlign: 'center',
  fontSize: '32px',
  marginBottom: '20px',
  color: '#00e676',
  textShadow: '0 0 10px rgba(0, 230, 118, 0.3)',
};

const cardsContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '20px',
  justifyContent: 'center',
};

const lessonCardStyle = {
  background: 'rgba(255, 255, 255, 0.05)',
  padding: '20px 30px',
  borderRadius: '16px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
  border: "1px solid rgba(0, 230, 118, 0.2)",
  fontSize: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const lessonContentStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
};

const addButtonContainerStyle = {
  marginBottom: '20px',
  display: 'flex',
  gap: '10px',
};

const addButtonStyle = {
  padding: '10px 20px',
  fontSize: '15px',
  background: 'linear-gradient(135deg, #00e676, #00c853)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const trialButtonStyle = {
  padding: '10px 20px',
  fontSize: '15px',
  background: 'linear-gradient(135deg, #0288d1, #40c4ff)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const editButtonStyle = {
  background: 'linear-gradient(135deg, #00e676, #00c853)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: '13px',
};

const deleteButtonStyle = {
  background: 'linear-gradient(135deg, #ff416c, #ff4b2b)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: '13px',
};

const editInputStyle = {
  padding: '8px',
  borderRadius: '6px',
  border: '1px solid #2a2a2a',
  background: 'rgba(255, 255, 255, 0.15)',
  color: '#f0f0f0',
  marginRight: '8px',
  width: '150px',
};

const saveButtonStyle = {
  background: 'linear-gradient(135deg, #00e676, #00c853)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: '13px',
  marginRight: '8px',
};

const cancelButtonStyle = {
  background: 'linear-gradient(135deg, #ff416c, #ff4b2b)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: '13px',
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: 'rgba(255, 255, 255, 0.05)',
  padding: '20px',
  borderRadius: '12px',
  width: '400px',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
  border: "1px solid rgba(0, 230, 118, 0.2)",
};

const modalTitleStyle = {
  fontSize: '20px',
  color: '#00e676',
  marginBottom: '20px',
  textAlign: 'center',
  textShadow: "0 0 8px rgba(0, 230, 118, 0.3)",
};

const modalInputStyle = {
  width: '100%',
  padding: '10px',
  marginBottom: '15px',
  borderRadius: '6px',
  border: '1px solid #2a2a2a',
  background: 'rgba(255, 255, 255, 0.15)',
  color: '#f0f0f0',
};

const modalButtonContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
};

const modalSaveButtonStyle = {
  background: 'linear-gradient(135deg, #00e676, #00c853)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '10px 20px',
  cursor: 'pointer',
  fontSize: '15px',
};

const modalCancelButtonStyle = {
  background: 'linear-gradient(135deg, #ff416c, #ff4b2b)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '10px 20px',
  cursor: 'pointer',
  fontSize: '15px',
};

const errorTextStyle = {
  marginTop: "20px",
  textAlign: "center",
  fontSize: "14px",
  color: "#f44336",
};

export default LessonSelectPage;